import { useMemo, useReducer } from 'react';
import { LayoutDefinition } from '../../../../infra/protected/Layout/LayoutDefinitions';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';
import type { ProjectItem, ProjectActivitiesState as ProjectActivitiesStateType, PageView } from '../types';

// Re-export for consumers
export type { ProjectActivitiesState } from '../types';

// Action types
export type ProjectActivitiesAction =
  | { type: 'SET_SHOW_LOADER'; payload: boolean }
  | { type: 'SET_SHOW_LOADER_DETAIL'; payload: boolean }
  | { type: 'SET_ROWS_DATA'; payload: ProjectItem[] }
  | { type: 'APPEND_ROWS_DATA'; payload: ProjectItem[] }
  | { type: 'SET_PAGE_VIEW'; payload: PageView }
  | { type: 'SET_SELECTED_KEYS'; payload: Set<string | number> | undefined }
  | { type: 'SET_DEFINITION'; payload: LayoutDefinition | null }
  | { type: 'SET_SHOW_MOBILE_DETAIL_VIEW'; payload: boolean }
  | { type: 'SET_SELECTED_ITEM'; payload: FPAData | null }
  | { type: 'SET_SELECTED_DATA_ROW'; payload: ProjectItem | Record<string, never> }
  | { type: 'SET_SHOW_ADD'; payload: boolean }
  | { type: 'SET_IS_MOVE_DIALOG_OPEN'; payload: boolean }
  | { type: 'SET_IS_DELETE_DIALOG_OPEN'; payload: boolean }
  | { type: 'SET_SHOW_FILTER'; payload: boolean }
  | { type: 'RESET_SELECTION' }
  | { type: 'RESET_STATE' };

// Initial state
const initialState: ProjectActivitiesStateType = {
  showLoader: true,
  showLoaderDetail: true,
  rowsData: [],
  pageView: 'Grid',
  selectedKeys: undefined,
  definition: null,
  showMobileDetailView: false,
  selectedItem: null,
  selectedDataRow: {},
  showAdd: false,
  isMoveDialogOpen: false,
  isDeleteDialogOpen: false,
  showFilter: false,
};

// Reducer function
function ProjectActivitiesReducer(state: ProjectActivitiesStateType, action: ProjectActivitiesAction): ProjectActivitiesStateType {
  switch (action.type) {
    case 'SET_SHOW_LOADER':
      return { ...state, showLoader: action.payload };
    case 'SET_SHOW_LOADER_DETAIL':
      return { ...state, showLoaderDetail: action.payload };
    case 'SET_ROWS_DATA':
      return { ...state, rowsData: action.payload };
    case 'APPEND_ROWS_DATA':
      return { ...state, rowsData: [...state.rowsData, ...action.payload] };
    case 'SET_PAGE_VIEW':
      return { ...state, pageView: action.payload };
    case 'SET_SELECTED_KEYS':
      return { ...state, selectedKeys: action.payload };
    case 'SET_DEFINITION':
      return { ...state, definition: action.payload };
    case 'SET_SHOW_MOBILE_DETAIL_VIEW':
      return { ...state, showMobileDetailView: action.payload };
    case 'SET_SELECTED_ITEM':
      return { ...state, selectedItem: action.payload };
    case 'SET_SELECTED_DATA_ROW':
      return { ...state, selectedDataRow: action.payload };
    case 'SET_SHOW_ADD':
      return { ...state, showAdd: action.payload };
    case 'SET_IS_MOVE_DIALOG_OPEN':
      return { ...state, isMoveDialogOpen: action.payload };
    case 'SET_IS_DELETE_DIALOG_OPEN':
      return { ...state, isDeleteDialogOpen: action.payload };
    case 'SET_SHOW_FILTER':
      return { ...state, showFilter: action.payload };
    case 'RESET_SELECTION':
      return {
        ...state,
        selectedKeys: undefined,
        selectedItem: null,
        selectedDataRow: {},
        definition: null,
      };
    case 'RESET_STATE':
      return { ...initialState };
    default:
      return state;
  }
}

// Custom hook to manage ProjectActivities state
export function useProjectActivitiesState() {
  const [state, dispatch] = useReducer(ProjectActivitiesReducer, initialState);

  // Action creators – memoized so useEffects depending on `actions` don't re-run every render
  const actions = useMemo(
    () => ({
      setShowLoader: (payload: boolean) => dispatch({ type: 'SET_SHOW_LOADER', payload }),
      setShowLoaderDetail: (payload: boolean) => dispatch({ type: 'SET_SHOW_LOADER_DETAIL', payload }),
      setRowsData: (payload: ProjectItem[]) => dispatch({ type: 'SET_ROWS_DATA', payload }),
      appendRowsData: (payload: ProjectItem[]) => dispatch({ type: 'APPEND_ROWS_DATA', payload }),
      setPageView: (payload: PageView) => dispatch({ type: 'SET_PAGE_VIEW', payload }),
      setSelectedKeys: (payload: Set<string | number> | undefined) => dispatch({ type: 'SET_SELECTED_KEYS', payload }),
      setDefinition: (payload: LayoutDefinition | null) => dispatch({ type: 'SET_DEFINITION', payload }),
      setShowMobileDetailView: (payload: boolean) => dispatch({ type: 'SET_SHOW_MOBILE_DETAIL_VIEW', payload }),
      setSelectedItem: (payload: FPAData | null) => dispatch({ type: 'SET_SELECTED_ITEM', payload }),
      setSelectedDataRow: (payload: ProjectItem | Record<string, never>) => dispatch({ type: 'SET_SELECTED_DATA_ROW', payload }),
      setShowAdd: (payload: boolean) => dispatch({ type: 'SET_SHOW_ADD', payload }),
      setIsMoveDialogOpen: (payload: boolean) => dispatch({ type: 'SET_IS_MOVE_DIALOG_OPEN', payload }),
      setIsDeleteDialogOpen: (payload: boolean) => dispatch({ type: 'SET_IS_DELETE_DIALOG_OPEN', payload }),
      setShowFilter: (payload: boolean) => dispatch({ type: 'SET_SHOW_FILTER', payload }),
      resetSelection: () => dispatch({ type: 'RESET_SELECTION' }),
      resetState: () => dispatch({ type: 'RESET_STATE' }),
    }),
    [dispatch]
  );

  return {
    state,
    dispatch,
    actions,
  };
}
